/**
 * Byte-size helpers shared by every attachment surface. Moved here from
 * `features/work-order/immotion-attachments/lib/attachmentFile.ts` so `shared/lib/fileType.ts` and
 * `pages/worker-appointment` can use them too — `shared` may not import from `features`.
 */
import type { ImmotionAttachment } from "@myapp/contract";

const stripBase64 = (base64: string) =>
  base64.replace(/^data:[^;]*;base64,/, "").replace(/\s/g, "");

/** Decoded size in bytes, derived from the base64 length — no decoding needed. */
export const attachmentByteSize = (attachment: ImmotionAttachment): number => {
  const clean = stripBase64(attachment.base64);
  const padding = clean.endsWith("==") ? 2 : clean.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((clean.length * 3) / 4) - padding);
};

/** "512 B" | "1,4 MB" | "2 GB" — German decimal comma, matching the rest of the app's numbers. */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0).replace(".", ",")} ${units[unit]}`;
};

/** Returns null when the payload is not decodable base64. */
export const attachmentToBlob = (attachment: ImmotionAttachment): Blob | null => {
  try {
    const binary = atob(stripBase64(attachment.base64));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: attachment.mime });
  } catch {
    return null;
  }
};

/**
 * Browsers block top-level navigation to `data:` URLs, so an attachment opened in a
 * new tab renders an empty page. A `blob:` URL navigates normally.
 */
export const createAttachmentUrl = (attachment: ImmotionAttachment): string | null => {
  const blob = attachmentToBlob(attachment);
  return blob ? URL.createObjectURL(blob) : null;
};
